import { Platform } from 'react-native';
import type { Trip } from '@/services/trips';
import type { ItineraryItem } from '@/services/itinerary';
import { applePlatformFlags } from '@/config/applePlatformFlags';
import {
  buildAppleIndexedTrips,
  type AppleIndexedTripSnapshot,
} from '@/services/applePlatformSnapshot';
import {
  buildNextTripWidgetPayload,
  buildTripSurfaceWidgetPayload,
  cacheNextTripWidgetPayload,
  cacheTripSurfaceSnapshot,
  readCachedTripSurfaceSnapshot,
} from '@/services/appleWidgetV1';
import { mergeTripSurfaceSnapshot } from '@/services/appleTripSurfaceMerge';
import { getCachedTripData, getCachedTrips, itineraryItemsFromOfflineCache } from '@/services/offlineTrips';
import { getTripDayContext, startOfDayInTimezone } from '@/utils/tripDayContext';
import { resolveTripSurfaceTimezone } from '@/utils/tripItemSchedule';

const APPLE_TRIP_SURFACE_RECENTLY_ENDED_MS = 7 * 24 * 60 * 60 * 1000;

export const pickAppleTripSurfaceTrip = (opts: {
  trips: Trip[];
  selectedTrip?: Trip | null;
  userId?: string | null;
  now?: Date;
}): Trip | null => {
  const now = opts.now ?? new Date();
  const candidates = opts.trips.filter((trip) => !trip.isArchived);
  if (opts.selectedTrip && !candidates.some((trip) => trip.id === opts.selectedTrip?.id)) {
    if (!opts.selectedTrip.isArchived) {
      candidates.push(opts.selectedTrip);
    }
  }
  const prioritizeOwner = (trip: Trip) => (trip.owner === opts.userId ? 0 : 1);
  const byStartDate = (a: Trip, b: Trip) => {
    const dateDiff = a.startDate.getTime() - b.startDate.getTime();
    return dateDiff !== 0 ? dateDiff : prioritizeOwner(a) - prioritizeOwner(b);
  };

  const inProgress = candidates
    .filter((trip) => getTripDayContext(trip, now).state === 'active')
    .sort(byStartDate);
  if (inProgress.length > 0) {
    return inProgress[0];
  }

  const upcomingOrCurrent = candidates
    .filter((trip) => {
      const context = getTripDayContext(trip, now);
      return context.state === 'upcoming' || context.state === 'active';
    })
    .sort(byStartDate);
  if (upcomingOrCurrent.length > 0) {
    return upcomingOrCurrent[0];
  }

  const recentlyEnded = candidates
    .filter((trip) => {
      const timeZone = resolveTripSurfaceTimezone(trip);
      const context = getTripDayContext(trip, now, { timeZone });
      if (context.state !== 'completed') return false;
      const endDay = startOfDayInTimezone(trip.endDate, timeZone);
      const elapsed = now.getTime() - endDay.getTime();
      return elapsed >= 0 && elapsed <= APPLE_TRIP_SURFACE_RECENTLY_ENDED_MS;
    })
    .sort((a, b) => {
      const dateDiff = b.endDate.getTime() - a.endDate.getTime();
      return dateDiff !== 0 ? dateDiff : prioritizeOwner(a) - prioritizeOwner(b);
    });
  return recentlyEnded[0] ?? null;
};

const loadSurfaceItineraryItems = async (
  surfaceTrip: Trip | null,
  selectedTrip: Trip | null | undefined,
  selectedItineraryItems: ItineraryItem[],
): Promise<ItineraryItem[]> => {
  if (!surfaceTrip?.id) return [];
  if (surfaceTrip.id === selectedTrip?.id && selectedItineraryItems.length > 0) {
    return selectedItineraryItems;
  }

  try {
    const { fetchTripItineraryItems } = await import('./itinerary');
    const liveItems = await fetchTripItineraryItems(surfaceTrip.id);
    if (liveItems.length > 0) {
      return liveItems;
    }
  } catch {
    // Fall back to offline cache below.
  }

  try {
    const cached = await getCachedTripData(surfaceTrip.id);
    return itineraryItemsFromOfflineCache(cached?.itinerary ?? [], surfaceTrip.id);
  } catch {
    return [];
  }
};

export type SyncAppleTripSurfaceSnapshotOpts = {
  trips: Trip[];
  selectedTrip?: Trip | null;
  selectedItineraryItems?: ItineraryItem[];
  userId?: string | null;
  indexedTrips?: AppleIndexedTripSnapshot[];
  now?: Date;
};

export const syncAppleTripSurfaceSnapshot = async (
  opts: SyncAppleTripSurfaceSnapshotOpts,
): Promise<void> => {
  if (Platform.OS !== 'ios') return;
  if (!applePlatformFlags.phase5Enabled || !applePlatformFlags.widgetNextTripEnabled) return;

  const surfaceTrip = pickAppleTripSurfaceTrip({
    trips: opts.trips,
    selectedTrip: opts.selectedTrip,
    userId: opts.userId,
    now: opts.now,
  });
  const surfaceItineraryItems = await loadSurfaceItineraryItems(
    surfaceTrip,
    opts.selectedTrip,
    opts.selectedItineraryItems ?? [],
  );
  const indexedTrips = opts.indexedTrips ?? buildAppleIndexedTrips(opts.trips, opts.now);

  const payload = buildNextTripWidgetPayload({
    nextTrip: surfaceTrip,
    itineraryItems: surfaceItineraryItems,
    now: opts.now,
  });
  const surface = buildTripSurfaceWidgetPayload({
    trip: surfaceTrip,
    itineraryItems: surfaceItineraryItems,
    indexedTrips,
    now: opts.now,
  });
  const existingSnapshot = await readCachedTripSurfaceSnapshot();
  const mergedSurface = mergeTripSurfaceSnapshot(surface, existingSnapshot);

  await Promise.all([cacheNextTripWidgetPayload(payload), cacheTripSurfaceSnapshot(mergedSurface)]);
};

export const syncAppleTripSurfaceSnapshotFromCache = async (userId: string): Promise<void> => {
  const trips = await getCachedTrips(userId);
  const surfaceTrip = pickAppleTripSurfaceTrip({ trips, userId });
  let selectedItineraryItems: ItineraryItem[] = [];
  if (surfaceTrip?.id) {
    try {
      const cached = await getCachedTripData(surfaceTrip.id);
      selectedItineraryItems = itineraryItemsFromOfflineCache(cached?.itinerary ?? [], surfaceTrip.id);
    } catch {
      selectedItineraryItems = [];
    }
  }
  await syncAppleTripSurfaceSnapshot({
    trips,
    userId,
    selectedTrip: surfaceTrip,
    selectedItineraryItems,
  });
};
